// ═══════════════════════════════════════════════════════════════════════════
// FeedImport — the history upload for one connection: UrbanPiper's Order
// Transactions Report (CSV or XLSX), read in the browser, previewed, then
// sent in parts (contract 5.11; plan Phase 1a; plan 7.5 item 11; appendix 7
// customer flow step 11 and "History and backfill"; appendix 6 correction 14).
// ═══════════════════════════════════════════════════════════════════════════
// Controlled like FeedMapping and FeedConflicts: no feedApi import. Props:
//   connectionId     the connection the report belongs to;
//   stores           feedApi listStores (store → location);
//   workspaces       feedApi getMappingTargets (zone, cutoff, routes, feed start);
//   allowFeedWindow  true on the super admin screen: offers the override that
//                    includes days on or after a route's feed start;
//   onImportChunk(rows, timeZone)  ONE request (feedApi.importOrderChunk):
//                    rows are lib/feedReport toUploadRow objects, at most 500;
//   onImported(summary, { complete })  after the last request (complete true),
//                    or after a failure part way, once something was sent.
//
// • Privacy: the file is read inside the change handler, and only the
//   projected, allow-listed rows (lib/feedReport parseReport) enter state,
//   plus the file row numbers of CSV lines skipped unread because their cells
//   don't line up with the header (an unquoted comma in an address). The
//   File, its text, the grid and the raw rows stay local to that handler, and
//   the file input is remounted so the File leaves the page too. Customer
//   columns are listed by name only. Nothing is logged or stored, and no
//   reader error text reaches the screen (it could echo a cell).
// • XLSX: read-excel-file is loaded with import() here only, the first time
//   an XLSX file is chosen (never the npm "xlsx" package). Numbers are read as
//   their text, so long IDs keep every digit.
// • The preview comes before anything is sent: per business day, location
//   and platform, counting orders, gross and net (plan 7.3), cancelled orders
//   (uploaded, not counted), fully discounted orders (a warning only), rows
//   left out and why, and the days on or after a route's feed start. Those
//   days are left out by default: until UrbanPiper confirms the report's ID is
//   the webhook's (contract open question 1), the server could count those
//   orders twice. A super admin can include them for a true-up.
// • Uploads run from the click handler only (StrictMode runs effects twice),
//   one request after another in file order. A failure stops the run; the
//   requests are idempotent, so uploading the file again finishes it. The
//   server runs one import at a time (429 rate_limited): the failure says so.
// • The .NET host stores the rows and applies them later, like live events,
//   while the connection is approved and not paused (FEED_ALIGN_STATUS.md
//   section 1, item 2), so the summary counts rows stored, never days rolled
//   up. Its warnings are English sentences (item 18) with no customer values:
//   counts, dates, zone ids, platform codes and day totals. They are shown as
//   plain text under a translated heading, each once, never passed through
//   t(): the first MAX_WARNINGS, then a button that lists the rest.
// ═══════════════════════════════════════════════════════════════════════════
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useT } from "../lib/i18n";
import { parseCSVGrid } from "../lib/csv";
import {
  MAX_FILE_BYTES, REQUIRED_HEADERS, SKIP_REASONS, parseReport, pickSheet, classifyRows, annotateRows, summarise,
  chunkRows, mergeImportResults, detectDateOrder, suggestImportZone, commonCurrency,
} from "../lib/feedReport";
import {
  platformInfo, importReasonKey, feedErrorCode, isValidTimeZone, timeZoneOptions, suggestedTimeZone, fmtDay, fmtMoney,
} from "../lib/feedUi";
import {
  inputStyle, adminThStyle, adminTdStyle, codeStyle, btnPrimaryDark, btnSecondary, smallBtn, disabledLook,
  mutedText, labelStyle, hintStyle, fieldErrorStyle, tableStyle, theadRowStyle, rowBorder, Banner, Chip, FeedErrorText,
} from "./feedKit";

const NONE = Object.freeze([]);
const ACCEPT = ".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
// Why a file couldn't be read: each is feed.import.readError.<code>.
export const READ_ERROR_CODES = Object.freeze([
  "wrongType", "xls", "tooBig", "readFailed", "readerUnavailable", "xlsxUnreadable", "empty", "noHeader", "noRows",
]);
const READ_ERRORS = new Set(READ_ERROR_CODES);
const PARSE_ERRORS = { empty: "empty", no_header: "noHeader", no_rows: "noRows" };
const SHOWN_ROW_NUMBERS = 12;   // file row numbers listed per reason
const MAX_TABLE_ROWS = 400;     // day rows shown in the preview table
const MAX_WARNINGS = 10;        // server notes listed after an upload

class ReadFailure extends Error {
  constructor(code) {
    super(code);
    this.code = code;
  }
}

// FileReader rather than Blob.text()/arrayBuffer(): it works in every
// browser this app supports and in the test environment.
function readBlob(blob, as) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new ReadFailure("readFailed"));
    if (as === "text") reader.readAsText(blob);
    else reader.readAsArrayBuffer(blob);
  });
}
// An XLSX file is a zip: it starts with "PK". (Windows reports .csv files as
// application/vnd.ms-excel, so the type can't decide.)
async function startsLikeZip(file) {
  const head = new Uint8Array(await readBlob(file.slice(0, 4), "buffer"));
  return head.length >= 2 && head[0] === 0x50 && head[1] === 0x4b;
}

// The file → { grid: rows of cells (string | Date | null), strictWidth }.
// strictWidth is true for CSV: lib/feedReport then skips a line whose cells
// don't line up with the header (an unquoted comma), unread.
async function readGrid(file) {
  const name = typeof file.name === "string" ? file.name.toLowerCase() : "";
  if (name.endsWith(".xls")) throw new ReadFailure("xls");
  const xlsxName = name.endsWith(".xlsx");
  if (!xlsxName && !name.endsWith(".csv")) throw new ReadFailure("wrongType");
  if (file.size > MAX_FILE_BYTES) throw new ReadFailure("tooBig");
  if (xlsxName || (await startsLikeZip(file))) {
    const buffer = await readBlob(file, "buffer");
    let readXlsx;
    try {
      readXlsx = (await import("read-excel-file/browser")).default;
    } catch (_) {
      throw new ReadFailure("readerUnavailable");
    }
    let sheets;
    try {
      sheets = await readXlsx(buffer, { parseNumber: (s) => s });
    } catch (_) {
      throw new ReadFailure("xlsxUnreadable");
    }
    return { grid: pickSheet(sheets), strictWidth: false };
  }
  const text = await readBlob(file, "text");
  return { grid: parseCSVGrid(String(text || "").replace(/^\uFEFF/, "")), strictWidth: true };
}

const distinct = (list) => [...new Set(list)];

export function FeedImport({ connectionId, stores = NONE, workspaces = NONE, allowFeedWindow = false, onImportChunk, onImported }) {
  const { t } = useT();
  const baseId = useId();
  const [inputKey, setInputKey] = useState(0);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState(null);   // a READ_ERRORS code
  const [report, setReport] = useState(null);         // { fileName, rows, skipped, customerColumns, unusedColumns, dateOrder }
  const [timeZone, setTimeZone] = useState("");
  const [dayFirst, setDayFirst] = useState(true);
  const [includeWindow, setIncludeWindow] = useState(false);
  const [run, setRun] = useState(null);               // { busy, done, total, error }
  const [result, setResult] = useState(null);         // merged summary of a finished upload
  const [allNotes, setAllNotes] = useState(false);    // every server note listed, not just the first MAX_WARNINGS
  const alive = useRef(true);
  const readSeq = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const busy = Boolean(run && run.busy);
  const zoneOk = isValidTimeZone(timeZone);
  const zoneForDays = zoneOk ? timeZone.trim() : null;

  const classified = useMemo(() => (report ? classifyRows(report.rows, { dayFirst }) : null), [report, dayFirst]);
  const annotated = useMemo(
    () => (classified ? annotateRows(classified.kept, { connectionId, stores, workspaces, timeZone: zoneForDays }) : NONE),
    [classified, connectionId, stores, workspaces, zoneForDays],
  );
  const groups = useMemo(() => summarise(annotated), [annotated]);

  const wsById = new Map(workspaces.filter((w) => w && w.id).map((w) => [w.id, w]));
  const fallbackCurrency = commonCurrency(stores, workspaces);
  const include = allowFeedWindow && includeWindow;
  const toSend = useMemo(() => (include ? annotated : annotated.filter((r) => !r.inFeedWindow)), [include, annotated]);
  const needsZone = annotated.some((r) => !r.hasOffset);
  const zoneProblem = needsZone && !zoneOk ? (timeZone.trim() ? "invalid" : "required") : null;
  const canUpload = Boolean(report) && toSend.length > 0 && !zoneProblem && !busy && typeof onImportChunk === "function";
  const partsPlanned = useMemo(() => chunkRows(toSend).length, [toSend]);

  // ── reading ───────────────────────────────────────────────────────────────
  async function onFile(e) {
    const file = e.target.files && e.target.files[0];
    setInputKey((k) => k + 1);   // a fresh, empty input: the File leaves the page
    if (!file || busy) return;
    const seq = ++readSeq.current;
    setReadError(null);
    setReport(null);
    setResult(null);
    setAllNotes(false);
    setRun(null);
    setIncludeWindow(false);
    setReading(true);
    try {
      const { grid, strictWidth } = await readGrid(file);
      const parsed = parseReport(grid, { strictWidth });
      if (seq !== readSeq.current || !alive.current) return;
      if (!parsed.ok) {
        setReadError(PARSE_ERRORS[parsed.error] || "readFailed");
        return;
      }
      const dateOrder = detectDateOrder(parsed.rows.map((r) => r.createdAt));
      setReport({
        fileName: typeof file.name === "string" ? file.name : "",
        rows: parsed.rows,
        skipped: parsed.skipped,   // { fileRow, reason } only: those lines were never read
        customerColumns: parsed.customerColumns,
        unusedColumns: parsed.unusedColumns,
        dateOrder,
      });
      setDayFirst(dateOrder !== "mdy");
      setTimeZone(suggestImportZone(stores, workspaces));
    } catch (err) {
      if (seq === readSeq.current && alive.current) {
        setReadError(err instanceof ReadFailure && READ_ERRORS.has(err.code) ? err.code : "readFailed");
      }
    } finally {
      if (seq === readSeq.current && alive.current) setReading(false);
    }
  }

  function reset() {
    if (busy) return;
    readSeq.current += 1;
    setReading(false);
    setReadError(null);
    setReport(null);
    setRun(null);
    setResult(null);
    setAllNotes(false);
    setIncludeWindow(false);
    setTimeZone("");
    setInputKey((k) => k + 1);
  }

  // ── uploading (click handler only) ────────────────────────────────────────
  async function upload() {
    if (!canUpload) return;
    const chunks = chunkRows(toSend);
    const zone = zoneOk ? timeZone.trim() : null;
    setRun({ busy: true, done: 0, total: chunks.length, error: null });
    const parts = [];
    let failure = null;
    for (let i = 0; i < chunks.length && alive.current; i++) {
      try {
        const answer = await onImportChunk(chunks[i].rows, zone);
        parts.push({ result: answer, fileRows: chunks[i].fileRows });
      } catch (err) {
        failure = err;
        break;
      }
      if (alive.current) setRun({ busy: true, done: i + 1, total: chunks.length, error: null });
    }
    if (!alive.current) return;
    const summary = mergeImportResults(parts);
    if (failure) {
      setRun({ busy: false, done: parts.length, total: chunks.length, error: failure });
      if (parts.length > 0 && onImported) onImported(summary, { complete: false });
      return;
    }
    setRun(null);
    setReport(null);
    setIncludeWindow(false);
    setResult(summary);
    setAllNotes(false);
    setInputKey((k) => k + 1);
    if (onImported) onImported(summary, { complete: true });
  }

  // ── derived figures for the preview ───────────────────────────────────────
  // Left out: lines that didn't line up (parseReport) and rows classifyRows
  // refused. Rows read = readable + left out.
  const skippedRows = classified ? [...report.skipped, ...classified.skipped] : NONE;
  const skippedByReason = SKIP_REASONS
    .map((reason) => ({ reason, rows: skippedRows.filter((s) => s.reason === reason).map((s) => s.fileRow).sort((a, b) => a - b) }))
    .filter((x) => x.rows.length > 0);
  const counting = annotated.filter((r) => r.counting);
  const cancelled = annotated.length - counting.length;
  const fullyDiscounted = groups.reduce((n, g) => n + g.fullyDiscounted, 0);
  const unmappedRows = annotated.filter((r) => !r.workspaceId).length;
  const newStores = distinct(annotated.filter((r) => !r.storeKnown).map((r) => `${r.providerStoreId}\n${r.providerBrandId || ""}`)).length;
  const unknownCodes = distinct(annotated.map((r) => r.platformCode).filter((c) => c && !platformInfo(c).known)).sort();
  const windowRows = annotated.filter((r) => r.inFeedWindow);
  const windowDays = distinct(windowRows.map((r) => r.day)).length;
  const zones = report ? timeZoneOptions() : NONE;
  const suggestion = report ? suggestedTimeZone(fallbackCurrency) : null;
  const askDayFirst = report && (report.dateOrder === "ambiguous" || report.dateOrder === "mixed");

  const fileId = `${baseId}-file`;
  const zoneId = `${baseId}-zone`;
  const zoneListId = `${baseId}-zones`;

  return (
    <div>
      <div style={{ fontSize:12.5, color:"#475569", lineHeight:1.55, marginBottom:6 }}>{t("feed.import.intro")}</div>
      <div style={{ ...hintStyle, marginTop:0, marginBottom:12 }}>{t("feed.import.privacy")}</div>

      <div>
        <label htmlFor={fileId} style={labelStyle}>{t("feed.import.file")}</label>
        <input key={inputKey} id={fileId} type="file" accept={ACCEPT} onChange={onFile} disabled={busy || reading}
          style={{ ...inputStyle, padding:"6px 8px", maxWidth:"100%" }} />
        <div style={hintStyle}>{t("feed.import.fileHint")}</div>
      </div>

      {reading && <div role="status" style={{ ...mutedText, marginTop:10 }}>{t("feed.import.reading")}</div>}
      {readError && (
        <Banner kind="error" style={{ marginTop:12 }}>
          <div>{t(`feed.import.readError.${readError}`)}</div>
          {readError === "noHeader" && (
            <div style={{ marginTop:4 }}>
              {t("feed.import.lookedFor")}
              {REQUIRED_HEADERS.map((h, i) => <span key={h}>{i > 0 ? ", " : ""}<code style={codeStyle}>{h}</code></span>)}
            </div>
          )}
        </Banner>
      )}

      {report && classified && (
        <div style={{ marginTop:16 }}>
          {/* ── What was read ─────────────────────────────────────────── */}
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center", fontSize:12.5, color:"#334155" }}>
            <span>{t("feed.import.fileLabel")}<b>{report.fileName || "—"}</b></span>
            <Chip tone="grey">{t("feed.import.rowsRead")}<span className="num">{report.rows.length + report.skipped.length}</span></Chip>
            <Chip tone="blue">{t("feed.import.rowsValid")}<span className="num">{classified.kept.length}</span></Chip>
            <Chip tone={skippedRows.length > 0 ? "amber" : "grey"}>{t("feed.import.rowsSkipped")}<span className="num">{skippedRows.length}</span></Chip>
          </div>
          {report.customerColumns.length > 0 && (
            <div style={{ ...hintStyle, color:"#0a7d4b" }}>
              {t("feed.import.customerRemoved")}
              {report.customerColumns.map((h, i) => <span key={`${h}-${i}`}>{i > 0 ? ", " : ""}<code style={codeStyle}>{h}</code></span>)}
            </div>
          )}
          {report.unusedColumns.length > 0 && (
            <div style={hintStyle}>
              {t("feed.import.unusedColumns")}
              {report.unusedColumns.map((h, i) => <span key={`${h}-${i}`}>{i > 0 ? ", " : ""}<code style={codeStyle}>{h}</code></span>)}
            </div>
          )}

          {skippedByReason.length > 0 && (
            <div style={{ marginTop:12 }}>
              <div style={labelStyle}>{t("feed.import.skippedTitle")}</div>
              {skippedByReason.map(({ reason, rows }) => (
                <div key={reason} style={{ fontSize:12, color:"#475569", lineHeight:1.6 }}>
                  <b>{t(importReasonKey(reason))}</b>
                  {" · "}{t("feed.import.fileRows")}
                  <span className="num">
                    {rows.slice(0, SHOWN_ROW_NUMBERS).join(", ")}
                    {rows.length > SHOWN_ROW_NUMBERS ? ` (+${rows.length - SHOWN_ROW_NUMBERS})` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* ── How to read the times ─────────────────────────────────── */}
          <div style={{ display:"flex", gap:18, flexWrap:"wrap", alignItems:"flex-start", marginTop:14 }}>
            <div style={{ flex:"1 1 260px", minWidth:0 }}>
              <label htmlFor={zoneId} style={labelStyle}>{t("feed.import.timeZone")}</label>
              <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                <input id={zoneId} value={timeZone} onChange={(e) => setTimeZone(e.target.value)} disabled={busy}
                  list={zones.length > 0 ? zoneListId : undefined} placeholder={suggestion || ""} spellCheck={false} autoComplete="off"
                  style={{ ...inputStyle, width:"100%", maxWidth:260 }} />
                {suggestion && timeZone.trim() !== suggestion && (
                  <button type="button" onClick={() => setTimeZone(suggestion)} disabled={busy} style={disabledLook(smallBtn, busy)}>
                    {t("feed.map.useZone")}{suggestion}
                  </button>
                )}
              </div>
              {zones.length > 0 && (
                <datalist id={zoneListId}>
                  {zones.map((z) => <option key={z} value={z} />)}
                </datalist>
              )}
              {zoneProblem === "required" && <div style={fieldErrorStyle}>{t("feed.import.timeZoneRequired")}</div>}
              {zoneProblem === "invalid" && <div style={fieldErrorStyle}>{t("feed.import.timeZoneInvalid")}</div>}
              {!zoneProblem && (
                <div style={hintStyle}>{needsZone ? t("feed.import.timeZoneHint") : t("feed.import.timeZoneOffsets")}</div>
              )}
            </div>
            {askDayFirst && (
              <div style={{ flex:"1 1 260px", minWidth:0, paddingTop:18 }}>
                <label style={{ display:"flex", gap:6, alignItems:"center", fontSize:12.5, color:"#334155", cursor: busy ? "default" : "pointer" }}>
                  <input type="checkbox" checked={dayFirst} disabled={busy} onChange={(e) => setDayFirst(e.target.checked)} />
                  {t("feed.import.dayFirst")}
                </label>
                <div style={hintStyle}>{t("feed.import.dayFirstHint")}</div>
              </div>
            )}
          </div>

          {/* ── Notes on what's in the file ───────────────────────────── */}
          <div style={{ display:"grid", gap:8, marginTop:14 }}>
            {cancelled > 0 && (
              <Banner kind="info">{t("feed.import.cancelledNote")}<b className="num">{cancelled}</b></Banner>
            )}
            {fullyDiscounted > 0 && (
              <Banner kind="warn">
                <div style={{ fontWeight:700 }}>{t("feed.import.fullyDiscountedCount")}<span className="num">{fullyDiscounted}</span></div>
                <div>{t("feed.import.fullyDiscountedWarn")}</div>
              </Banner>
            )}
            {unmappedRows > 0 && (
              <Banner kind="info">
                <div>{t("feed.import.unmappedNote")}<b className="num">{unmappedRows}</b></div>
                {newStores > 0 && <div>{t("feed.import.newStores")}<b className="num">{newStores}</b></div>}
                <div>{t("feed.import.unmappedHint")}</div>
              </Banner>
            )}
            {unknownCodes.length > 0 && (
              <Banner kind="warn">
                <div>
                  {t("feed.import.unknownPlatforms")}
                  {unknownCodes.map((c, i) => <span key={c}>{i > 0 ? ", " : ""}<code style={codeStyle}>{c}</code></span>)}
                </div>
                <div>{t("feed.import.unknownPlatformsHint")}</div>
              </Banner>
            )}
            {windowRows.length > 0 && (
              <Banner kind="warn">
                <div style={{ fontWeight:700 }}>
                  {t("feed.import.windowTitle")}<span className="num">{windowDays}</span>
                  {" · "}{t("feed.import.windowRows")}<span className="num">{windowRows.length}</span>
                </div>
                <div>{t("feed.import.windowBody")}</div>
                {allowFeedWindow && (
                  <label style={{ display:"flex", gap:6, alignItems:"center", marginTop:8, fontWeight:600, cursor: busy ? "default" : "pointer" }}>
                    <input type="checkbox" checked={includeWindow} disabled={busy} onChange={(e) => setIncludeWindow(e.target.checked)} />
                    {t("feed.import.windowInclude")}
                  </label>
                )}
                {include && <div style={{ marginTop:6 }}>{t("feed.import.windowIncluded")}</div>}
              </Banner>
            )}
          </div>

          {/* ── Orders by day ─────────────────────────────────────────── */}
          <div style={{ marginTop:16 }}>
            <div style={labelStyle}>{t("feed.import.daysTitle")}</div>
            <div style={{ ...hintStyle, marginTop:0, marginBottom:8 }}>{t("feed.import.daysHint")}</div>
            <div style={{ overflowX:"auto" }}>
              <table style={tableStyle}>
                <thead>
                  <tr style={theadRowStyle}>
                    <th style={adminThStyle}>{t("feed.import.col.day")}</th>
                    <th style={adminThStyle}>{t("feed.import.col.location")}</th>
                    <th style={adminThStyle}>{t("feed.import.col.platform")}</th>
                    <th style={{ ...adminThStyle, textAlign:"end" }}>{t("feed.import.col.orders")}</th>
                    <th style={{ ...adminThStyle, textAlign:"end" }}>{t("feed.import.col.gross")}</th>
                    <th style={{ ...adminThStyle, textAlign:"end" }}>{t("feed.import.col.net")}</th>
                    <th style={{ ...adminThStyle, textAlign:"end" }}>{t("feed.import.col.cancelled")}</th>
                    <th style={adminThStyle}>{t("feed.import.col.notes")}</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.slice(0, MAX_TABLE_ROWS).map((g, i) => {
                    const w = g.workspaceId ? wsById.get(g.workspaceId) : null;
                    const currency = w ? w.currency : fallbackCurrency;
                    const info = platformInfo(g.platformCode);
                    return (
                      <tr key={`${g.day}-${g.workspaceId || ""}-${g.platformCode || ""}`} style={{ ...rowBorder(i), background: g.inFeedWindow && !include ? "#fffbeb" : "transparent" }}>
                        <td className="num" style={{ ...adminTdStyle, whiteSpace:"nowrap", fontWeight:600, color:"#0f172a" }}>{fmtDay(g.day)}</td>
                        <td style={adminTdStyle}>{w ? (w.name || w.id) : <span style={mutedText}>{t("feed.import.notMapped")}</span>}</td>
                        <td style={adminTdStyle}>
                          {g.platformCode ? (
                            <>
                              <div>{info.channelName || g.platformCode}</div>
                              {info.channelName && <code style={codeStyle}>{g.platformCode}</code>}
                            </>
                          ) : <span style={mutedText}>{t("feed.import.noPlatform")}</span>}
                        </td>
                        <td className="num" style={{ ...adminTdStyle, textAlign:"end" }}>{g.orders}</td>
                        <td className="num" style={{ ...adminTdStyle, textAlign:"end" }}>{fmtMoney(g.gross, currency)}</td>
                        <td className="num" style={{ ...adminTdStyle, textAlign:"end" }}>{fmtMoney(g.net, currency)}</td>
                        <td className="num" style={{ ...adminTdStyle, textAlign:"end" }}>{g.cancelled}</td>
                        <td style={{ ...adminTdStyle, maxWidth:260 }}>
                          <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                            {g.inFeedWindow && <Chip tone="amber">{t("feed.import.chip.window")}</Chip>}
                            {g.inFeedWindow && !include && <Chip tone="grey">{t("feed.import.chip.excluded")}</Chip>}
                            {g.fullyDiscounted > 0 && <Chip tone="amber">{t("feed.import.chip.fullyDiscounted")}<span className="num">{g.fullyDiscounted}</span></Chip>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {groups.length > MAX_TABLE_ROWS && <div style={{ ...mutedText, marginTop:6 }}>{t("feed.import.tableCapped")}</div>}
          </div>

          {/* ── Upload ────────────────────────────────────────────────── */}
          {run && run.error && (
            <Banner kind="error" style={{ marginTop:12 }}>
              <div><FeedErrorText error={run.error} /></div>
              {feedErrorCode(run.error) === "rate_limited" && <div style={{ marginTop:4 }}>{t("feed.import.rateLimitedHint")}</div>}
              <div style={{ marginTop:4 }}>
                {t("feed.import.failedParts")}<b className="num">{run.done}</b>{t("feed.import.of")}<b className="num">{run.total}</b>
              </div>
              <div>{t("feed.import.failedRetry")}</div>
            </Banner>
          )}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:10, flexWrap:"wrap", marginTop:12 }}>
            {busy ? (
              <span role="status" style={{ fontSize:12, color:"#64748b" }}>
                {t("feed.import.progress")}<span className="num">{Math.min(run.done + 1, run.total)}</span>{t("feed.import.of")}<span className="num">{run.total}</span>
              </span>
            ) : (
              <span style={{ fontSize:12, color:"#64748b" }}>
                {toSend.length === 0
                  ? t("feed.import.nothingToUpload")
                  : (
                    <>
                      {t("feed.import.toUpload")}<span className="num" style={{ fontWeight:700, color:"#0f172a" }}>{toSend.length}</span>
                      {partsPlanned > 1 && <> · {t("feed.import.parts")}<span className="num">{partsPlanned}</span></>}
                    </>
                  )}
              </span>
            )}
            <button type="button" onClick={reset} disabled={busy} style={disabledLook(btnSecondary, busy)}>{t("feed.cancel")}</button>
            <button type="button" onClick={upload} disabled={!canUpload} style={disabledLook(btnPrimaryDark, !canUpload)}>
              {busy ? t("feed.import.uploading") : t("feed.import.upload")}
            </button>
          </div>
        </div>
      )}

      {/* ── The server's summary: rows stored, applied later ─────────── */}
      {result && (
        <div style={{ marginTop:16 }}>
          <Banner kind="ok">
            <div style={{ fontWeight:700 }}>{t("feed.import.doneTitle")}</div>
            <div style={{ marginTop:4 }}>{t("feed.import.doneStored")}</div>
            <div className="num" style={{ display:"flex", gap:"4px 14px", flexWrap:"wrap", marginTop:6 }}>
              <span>{t("feed.import.res.received")}<b>{result.received}</b></span>
              <span>{t("feed.import.res.inserted")}<b>{result.inserted}</b></span>
              <span>{t("feed.import.res.unchanged")}<b>{result.unchanged}</b></span>
              <span>{t("feed.import.res.rejected")}<b>{result.rejected.length}</b></span>
              <span>{t("feed.import.res.storesNew")}<b>{result.storesNew}</b></span>
            </div>
            {result.storesNew > 0 && <div style={{ marginTop:4 }}>{t("feed.import.storesNewHint")}</div>}
          </Banner>
          {result.warnings.length > 0 && (
            <div style={{ marginTop:12 }}>
              <div style={labelStyle}>{t("feed.import.warningsTitle")}</div>
              {/* The server's own English sentences, as data: never through t(). */}
              <ul lang="en" dir="ltr" style={{ margin:0, paddingInlineStart:20, fontSize:12, color:"#475569", lineHeight:1.6 }}>
                {(allNotes ? result.warnings : result.warnings.slice(0, MAX_WARNINGS)).map((w) => <li key={w}>{w}</li>)}
              </ul>
              {/* Every note can be read: one that matters for the money (typed
                  rows larger than the orders replacing them) may come late. */}
              {!allNotes && result.warnings.length > MAX_WARNINGS && (
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginTop:6 }}>
                  <span style={mutedText}>{t("feed.import.warningsMore")}<span className="num">{result.warnings.length - MAX_WARNINGS}</span></span>
                  <button type="button" onClick={() => setAllNotes(true)} style={smallBtn}>{t("feed.import.warningsShowAll")}</button>
                </div>
              )}
            </div>
          )}
          {result.rejected.length > 0 && (
            <div style={{ marginTop:12 }}>
              <div style={labelStyle}>{t("feed.import.rejectedTitle")}</div>
              <div style={{ overflowX:"auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr style={theadRowStyle}>
                      <th style={adminThStyle}>{t("feed.import.col.fileRow")}</th>
                      <th style={adminThStyle}>{t("feed.import.col.reason")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rejected.slice(0, MAX_TABLE_ROWS).map((r, i) => (
                      <tr key={`${r.fileRow}-${i}`} style={rowBorder(i)}>
                        <td className="num" style={adminTdStyle}>{r.fileRow == null ? "—" : r.fileRow}</td>
                        <td style={adminTdStyle}>{t(importReasonKey(r.reason))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {result.rejected.length > MAX_TABLE_ROWS && <div style={{ ...mutedText, marginTop:6 }}>{t("feed.import.tableCapped")}</div>}
            </div>
          )}
          <div style={{ display:"flex", justifyContent:"flex-end", marginTop:12 }}>
            <button type="button" onClick={reset} style={btnSecondary}>{t("feed.import.another")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
